// ============================================================================
// OTA HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { otaService } from 'api/services/otaService';
import type {
  Ota,
  CreateOtaRequest,
  UpdateOtaRequest,
  OtaSearchRequest,
  ID,
} from 'api/types';

export const useOtas = (params?: OtaSearchRequest) => {
  const [data, setData] = useState<Ota[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchOtas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await otaService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchOtas();
  }, [fetchOtas]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchOtas,
  };
};

export const useOta = (id: ID) => {
  const [data, setData] = useState<Ota | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOta = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await otaService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOta();
  }, [fetchOta]);

  return { data, loading, error, refetch: fetchOta };
};

export const useCreateOta = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createOta = async (
    data: CreateOtaRequest,
    options?: { onSuccess?: (ota: Ota) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await otaService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { createOta, loading, error, success };
};

export const useUpdateOta = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateOta = async (
    id: ID,
    data: UpdateOtaRequest,
    options?: { onSuccess?: (ota: Ota) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await otaService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { updateOta, loading, error, success };
};

export const useDeleteOta = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteOta = async (
    id: ID,
    rowVersion: number,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await otaService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { deleteOta, loading, error, success };
};
